import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, LogOut, Search, Star, X, Languages, HelpCircle, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { navForRole, flattenNav } from "./navConfig";
import { useNavCounts } from "./useNavCounts";
import { usePinnedNav } from "./usePinnedNav";

function Avatar({ name }) {
    const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div className="avatar-ring h-12 w-12">
            <div className="h-full w-full flex items-center justify-center text-sm font-semibold text-primary">{initials}</div>
        </div>
    );
}

function CountBadge({ value }) {
    if (!value) return null;
    return (
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold" data-testid="nav-count-badge">
            {value > 99 ? "99+" : value}
        </span>
    );
}

// Walk the role config and produce a flat array of rows. Each row has a stable
// `key`, a `depth`, and a `kind` of "leaf" or "parent". Parents have a
// `parentKey` that descendants reference so we can hide them when collapsed.
function buildFlatRows(groups) {
    const rows = [];
    const walk = (items, depth, parentKey) => {
        for (const item of items || []) {
            const childItems = item.items || item.children;
            const isParent = !!(childItems && childItems.length);
            const key = (item.to || item.labelKey) + (item.end ? ":exact" : "") + ":" + depth;
            rows.push({ key, item, depth, kind: isParent ? "parent" : "leaf", parentKey });
            if (isParent) walk(childItems, depth + 1, key);
        }
    };
    for (const group of groups || []) {
        rows.push({ key: "group:" + group.label, kind: "group", group });
        walk(group.items, 0, null);
    }
    return rows;
}

// Where the brand link in the top rail should point per role.
function defaultHomeForRole(role) {
    if (!role) return "/";
    if (role.startsWith("PROVIDER_") || role === "PROVIDER_EXPERT") return "/provider/dashboard";
    if (role === "MEMBER") return "/member/home";
    if (role === "SALES_AGENT") return "/kiosk/sales";
    return "/admin/dashboard";
}

export default function Sidebar({ onNavigate }) {
    const { user, logout } = useAuth();
    const { t, lang, isRTL, toggle: toggleLang } = useI18n();
    const location = useLocation();
    const counts = useNavCounts();
    const pin = usePinnedNav();
    const [search, setSearch] = useState("");
    const [openSet, setOpenSet] = useState(() => new Set());

    const groups = useMemo(() => (user ? navForRole(user.role) : []), [user]);
    const allLeaves = useMemo(() => flattenNav(groups), [groups]);
    const rows = useMemo(() => buildFlatRows(groups), [groups]);

    // Auto-expand any parent whose path is currently active.
    useEffect(() => {
        const next = new Set(openSet);
        let changed = false;
        for (const row of rows) {
            if (row.kind !== "parent") continue;
            const childItems = row.item.items || row.item.children || [];
            const active = childItems.some((c) => location.pathname.startsWith((c.to || "").split("?")[0] || "__never__"));
            if (active && !next.has(row.key)) { next.add(row.key); changed = true; }
        }
        if (changed) setOpenSet(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, rows]);

    const pinnedItems = useMemo(() => {
        const byTo = new Map(allLeaves.map((it) => [it.to, it]));
        return pin.pinned.map((to) => byTo.get(to)).filter(Boolean);
    }, [pin.pinned, allLeaves]);

    const filteredLeaves = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return null;
        return allLeaves.filter((it) => (t(it.labelKey) || "").toLowerCase().includes(q));
    }, [search, allLeaves, t]);

    if (!user) return null;
    const sideClass = isRTL ? "border-l" : "border-r";

    const toggleOpen = (key) => setOpenSet((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Filter rows: hide a row if any ancestor parent is collapsed.
    const visibleRows = [];
    for (const row of rows) {
        if (row.kind === "group") { visibleRows.push(row); continue; }
        if (!row.parentKey || openSet.has(row.parentKey)) {
            visibleRows.push(row);
        }
    }

    const renderItemRow = (row, opts = {}) => {
        const item = row.item;
        const Icon = item.Icon;
        const padStyle = row.depth > 0 ? { paddingInlineStart: (row.depth * 12 + 12) + "px" } : undefined;
        const badgeValue = item.badge && item.badge.source ? counts[item.badge.source] : 0;

        if (row.kind === "leaf") {
            const navClass = (state) => "nav-row group/nav " + (state.isActive ? "active" : "");
            return (
                <NavLink
                    key={row.key}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={navClass}
                    style={padStyle}
                    data-testid={item.testId}
                >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="truncate flex-1">{t(item.labelKey)}</span>
                    {badgeValue ? <CountBadge value={badgeValue} /> : null}
                    {pin && !opts.isInPinnedSection ? (
                        <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); pin.toggle(item.to); }}
                            className={"shrink-0 hover:text-amber-500 transition-opacity " + (pin.isPinned(item.to) ? "text-amber-500 opacity-100" : "text-muted-foreground opacity-0 group-hover/nav:opacity-100")}
                            aria-label={pin.isPinned(item.to) ? "Unpin" : "Pin"}
                            title={pin.isPinned(item.to) ? "Unpin" : "Pin"}
                        >
                            <Star className={"h-3.5 w-3.5 " + (pin.isPinned(item.to) ? "fill-current" : "")} />
                        </button>
                    ) : null}
                </NavLink>
            );
        }

        // parent
        const isOpen = openSet.has(row.key);
        const childItems = item.items || item.children || [];
        const childActive = childItems.some((c) => location.pathname.startsWith((c.to || "").split("?")[0] || "__never__"));
        const btnClass = "nav-row group/nav w-full " + (childActive ? "active" : "");
        return (
            <button
                key={row.key}
                type="button"
                onClick={() => toggleOpen(row.key)}
                className={btnClass}
                style={padStyle}
                data-testid={(item.testId || "nav") + "-toggle"}
                aria-expanded={isOpen}
            >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate flex-1 text-start">{t(item.labelKey)}</span>
                <ChevronDown className={"h-4 w-4 transition-transform " + (isOpen ? "rotate-180" : "")} />
            </button>
        );
    };

    return (
        <aside
            className={"hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-[280px] bg-card " + sideClass + " border-border z-30"}
            data-testid="sidebar-desktop"
        >
            {/* Brand */}
            <NavLink
                to={defaultHomeForRole(user.role)}
                className="flex items-center gap-2 px-5 py-5 border-b border-border hover:bg-secondary/30 transition-colors"
                data-testid="sidebar-brand"
            >
                <img src="/hemaia_logo.svg" alt="Hemaia" className="h-8" />
            </NavLink>

            <div className="px-3 pt-3">
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("nav.searchPlaceholder")}
                        className="w-full ps-9 pe-8 py-2 text-sm rounded-xl bg-secondary/50 border border-transparent focus:bg-card focus:border-border focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="sidebar-search"
                    />
                    {search ? (
                        <button type="button" onClick={() => setSearch("")} className="absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
                {filteredLeaves ? (
                    <div>
                        <p className="px-3 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                            {t("nav.searchResults", { n: filteredLeaves.length })}
                        </p>
                        <div className="space-y-1">
                            {filteredLeaves.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-muted-foreground">{t("nav.noResults")}</p>
                            ) : null}
                            {filteredLeaves.map((it) => renderItemRow({ key: it.to, kind: "leaf", item: it, depth: 0 }))}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {visibleRows.map((row) => {
                            if (row.kind === "group") {
                                const actions = row.group.actions || [];
                                return (
                                    <div key={row.key} className="px-3 mt-4 mb-2 flex items-center justify-between">
                                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                            {t(row.group.label)}
                                        </p>
                                        {actions.length > 0 ? (
                                            <div className="flex items-center gap-0.5">
                                                {actions.map((a, i) => {
                                                    const ActionIcon = a.icon;
                                                    return (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            onClick={a.onClick}
                                                            title={a.tooltip ? t(a.tooltip) : undefined}
                                                            className="h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                                                            data-testid={a.testId}
                                                        >
                                                            <ActionIcon className="h-3.5 w-3.5" />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            }
                            return renderItemRow(row);
                        })}

                        {pinnedItems.length > 0 ? (
                            <div data-testid="sidebar-pinned" className="pt-2">
                                <p className="px-3 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
                                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                                    {t("groups.pinned")}
                                </p>
                                <div className="space-y-1">
                                    {pinnedItems.map((it) => renderItemRow({ key: "pinned-" + it.to, kind: "leaf", item: it, depth: 0 }, { isInPinnedSection: true }))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </nav>

            {/* Bottom rail: help link + user profile popover */}
            <div className="border-t border-border p-3 space-y-1.5">
                <a
                    href="mailto:support@hemaia.com"
                    className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                    data-testid="sidebar-help"
                >
                    <HelpCircle className="h-4 w-4 shrink-0" />
                    {t("nav.helpAndSupport")}
                </a>

                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="w-full flex items-center gap-3 p-2 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                            data-testid="sidebar-user-popover-trigger"
                        >
                            <Avatar name={user.full_name} />
                            <div className="min-w-0 flex-1 text-start">
                                <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.full_name}</p>
                                <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-user-role">{t("roles." + user.role)}</p>
                            </div>
                            <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="start" className="w-[260px] p-1" data-testid="sidebar-user-popover">
                        <button
                            type="button"
                            onClick={toggleLang}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors"
                            data-testid="sidebar-language-toggle"
                        >
                            <Languages className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="flex-1 text-start">{lang === "en" ? t("common.arabic") : t("common.english")}</span>
                            <span className="text-xs text-muted-foreground">{lang === "en" ? "AR" : "EN"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => { /* future: open settings */ }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors text-muted-foreground"
                            data-testid="sidebar-settings"
                            disabled
                        >
                            <SettingsIcon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-start">{t("nav.accountSettings")}</span>
                            <span className="text-[10px] uppercase tracking-wide opacity-60">{t("common.comingSoon")}</span>
                        </button>
                        <div className="my-1 h-px bg-border" />
                        <button
                            type="button"
                            onClick={logout}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/5 transition-colors"
                            data-testid="sidebar-logout-button"
                        >
                            <LogOut className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-start">{t("common.logout")}</span>
                        </button>
                    </PopoverContent>
                </Popover>
            </div>
        </aside>
    );
}
